
import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Link } from 'react-router-dom';
import { ResellerPlanDetails } from '@/hooks/license/useResellerPlans';
import { formatCurrency } from '@/utils/formatters';

interface OrderSuccessViewProps {
  planDetails: ResellerPlanDetails | null;
  orderDetails: {
    orderNumber: string;
    setupFee: number;
    discount: number;
    totalAmount: number;
    promoCode: string;
    annualPrice: number;
  };
}

const OrderSuccessView: React.FC<OrderSuccessViewProps> = ({
  planDetails,
  orderDetails
}) => {
  return (
    <div className="w-full max-w-2xl">
      <div className="flex flex-col items-center text-center mb-8">
        <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Your Order Was Successful!</h1>
        <p className="text-gray-600 max-w-md">
          Thank you for your purchase! Your reseller account is being set up and you will receive an email with further instructions.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Order Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-sm text-gray-500">Order Number</p>
              <p className="font-semibold">{orderDetails.orderNumber}</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-2">
                {planDetails?.name || 'Reseller Plan'}
              </h3>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>One-time Setup Fee</span>
                  <span>${formatCurrency(orderDetails.setupFee)}</span>
                </div>
                
                {orderDetails.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount {orderDetails.promoCode && `(${orderDetails.promoCode})`}</span>
                    <span>-${formatCurrency(orderDetails.discount)}</span>
                  </div>
                )}
                
                <Separator />
                
                <div className="flex justify-between font-semibold">
                  <span>Total Paid Today</span>
                  <span>${formatCurrency(orderDetails.totalAmount)}</span>
                </div>
                
                <div className="mt-4 pt-4 border-t border-dashed">
                  <p className="font-medium text-gray-700 mb-2">Annual Subscription Details:</p>
                  <div className="flex justify-between text-sm">
                    <span>Annual Subscription Fee</span>
                    <span>${formatCurrency(orderDetails.annualPrice)}/year</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span>First Payment Due</span>
                    <span>{new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 bg-blue-50 p-2 rounded">
                    Your annual subscription will begin after your account is fully set up. You will receive an invoice 
                    before the first payment is processed.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-md text-sm">
              <p>We've sent a confirmation email to your inbox with all the details of your purchase.</p>
            </div>
          </div>
          
          <div className="mt-8 flex justify-center">
            <Button asChild>
              <Link to="/" className="flex items-center">
                Return to Home <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderSuccessView;
